import * as helpers from "./helpers";
import * as globalVariables from "./globalVariables";
import translate from "./translate";
import Promotions from "./Promotions";
import Templater from "./Templater";

export default (options) => {
  let isKeyboardNavigationOn = false;
  let closeMobileNavInterval;

  const {
    statistic,
    parentSender,
    parentReceiver,
    mainSliderContainer,
    carousel,
    widgetVersion = "iFrame"
  } = options;

  if (!mainSliderContainer) throw new Error("slider container not found");
  if (!statistic) throw new Error("statistic have to be defined");
  if (!parentSender) throw new Error("parentSender have to be defined");
  if (!parentReceiver) throw new Error("parentReceiver have to be defined");
  if (!carousel) throw new Error("carousel have to be defined");

  let globalIDAnimationFrame;
  let updateLayoutTimeout;
  let isIFrameHeightSet =
    widgetVersion === "iFrame" || globalVariables.widgetHeight();
  let widgetDocumentLoaded = false;

  if (!isIFrameHeightSet) {
    document.documentElement.classList.add("autoHeight");
  }

  /**
   * Temp. solution to prevent breaking existing widgets:
   * Receive info if the widget height was configured in admin panel or is this the "auto" height based on the widget content.
   * Though this info can be get from the globalVariables.widgetHeight(),
   * override it in case the height was set in outer container manually, not through the admin panel, and thus is not stored in widget info.
   */

  parentReceiver.subscribe((event) => {
    if (
      event.data.from === "dealsParentWindow" &&
      event.data.hasOwnProperty("isIFrameHeightSet")
    ) {
      isIFrameHeightSet = event.data.isIFrameHeightSet;

      if (event.data.isIFrameHeightSet) {
        document.documentElement.classList.remove("autoHeight");
      } else {
        document.documentElement.classList.add("autoHeight");
      }
    }
  });

  parentReceiver.subscribe((event) => {
    if (event.data.from === "dealsParentWindow" && event.data.originUrl) {
      statistic.parentOriginUrl = event.data.originUrl;
      statistic.parentPlatformType =
        event.data.originWindowWidth <= 720 ? "web_mobile" : "web";
      parentSender.parentOrigin = event.origin;
    }
  });

  const defaultItemsPerPage =
    parseInt(mainSliderContainer.getAttribute("data-items-per-page"), 10) || 3;

  let itemsPerPage = defaultItemsPerPage;
  let sliderInitialized = false;
  let widgetViewSent = false;
  let firstPromotionsIds = [];

  /* Show error message and stop app when user's browser doesn't support css3 transform property. */
  if (!helpers.supports("transform")) {
    helpers.showMessage({
      message: translate("error_outdated_browser"),
      type: "error"
    });

    throw new Error(translate("error_outdated_browser"));
  }

  const promotions = new Promotions({
    fetcher: helpers.fetcher,
    perItems: itemsPerPage,
    preloadItems: itemsPerPage * 2
  });

  const templater = new Templater({
    mainColor: globalVariables.mainColor(),
    landingPageUrl: globalVariables.newspaperLandingPageUrl(),
    newspaperName: globalVariables.newspaperName(),
    hexToRgba: helpers.hexToRgbA,
    contactsEnabled: globalVariables.widgetContactsEnabled()
  });

  const generatePromotionsNodes = (data) =>
    Promise.resolve(data.promotions.map((item) => templater.tile(item)));

  const generateErrorMessage = (error) => {
    console.error(error);
    helpers.showMessage({
      message: error.message,
      type: "error"
    });
  };

  const getFirstPromotionsIds = (data) => {
    const { promotionsIds } = data;

    if (!sliderInitialized) {
      firstPromotionsIds = promotionsIds.slice(0, itemsPerPage);
    }

    return data;
  };

  const initTilesResponsiveness = (...params) => {
    if (!widgetDocumentLoaded) {
      const waitDocument = setInterval(() => {
        if (widgetDocumentLoaded) {
          clearInterval(waitDocument);
          initTilesResponsiveness(...params);
        }
      }, 300);
    } else {
      Templater.setTilesResponsiveness(...params);
      mainSliderContainer.classList.add("initial-tiles-scaling-finished");
    }
  };

  const sendWidgetDimensions = () => {
    if (isIFrameHeightSet) return;

    parentSender.ready().then(() => {
      parentSender.send({
        widgetDimensions: {
          width: document.body.offsetWidth,
          height: document.body.offsetHeight
        }
      });
    });
  };

  const updateLayoutBasedOnItemsPerPage = () => {
    if (window.innerWidth === 0) {
      clearTimeout(updateLayoutTimeout);
      updateLayoutTimeout = setTimeout(updateLayoutBasedOnItemsPerPage, 1000);
      return;
    } else if (window.innerWidth <= 560) {
      itemsPerPage = 1;
    } else if (
      window.innerWidth > 560 &&
      (defaultItemsPerPage == 2 ||
        (defaultItemsPerPage == 3 && window.innerWidth <= 720))
    ) {
      itemsPerPage = 2;
    } else if (window.innerWidth > 720 && defaultItemsPerPage == 3) {
      itemsPerPage = 3;
    }

    promotions.setPerItems(itemsPerPage);
    carousel.setTotalPages(promotions.promotionsLength() / itemsPerPage);
    carousel.setPerPage(itemsPerPage);
  };

  window.addEventListener("load", () => {
    widgetDocumentLoaded = true;
  });

  /**
   * On resize:
   * - recalculate how many slides to show: 1, 2, 3;
   * - update tiles responsiveness;
   * - send new widget dimensions to parent.
   */

  window.addEventListener(
    "resize",
    (e) => {
      if (globalIDAnimationFrame) {
        window.cancelAnimationFrame(globalIDAnimationFrame);
        globalIDAnimationFrame = null;
      }

      globalIDAnimationFrame = window.requestAnimationFrame(() => {
        updateLayoutBasedOnItemsPerPage();
        sendWidgetDimensions();
        carousel.refreshSlider(true);
        carousel.setCurrentSlide(1);

        Templater.resetTilesResponsiveness(mainSliderContainer);
        Templater.setTilesResponsiveness(
          mainSliderContainer,
          isIFrameHeightSet
        );
      });
    },
    false
  );

  const isElOverflown = ({ clientWidth, scrollWidth }) => {
    return scrollWidth > clientWidth;
  };

  const scrollNavToActiveEl = (nav, el) => {
    nav.firstChild.scroll({
      left: el.getBoundingClientRect().left - 16,
      behavior: "smooth"
    });
  };

  const handleHeaderNavToggle = (mobileNav) => {
    const mobileNavBtn = mobileNav.getElementsByClassName(
      "js-header-nav-mobile-btn"
    )[0];
    const mobileNavContent = mobileNav.getElementsByClassName(
      "js-header-nav-mobile-content"
    )[0];

    const openMobileNav = () => {
      mobileNavBtn.setAttribute("aria-expanded", "true");
      mobileNav.classList.add("open");
      document.addEventListener("click", closeNavOnClickOutside);
      document.addEventListener("keyup", closeNavOnClickOutside);
      document.addEventListener("keyup", closeOnEsc);

      // For mouse navigation: close mobile nav after 3 seconds of inactivity.
      if (!isKeyboardNavigationOn) {
        closeMobileNavInterval = setInterval(() => {
          const isHover = mobileNav.parentElement.querySelector(':hover') === mobileNav;
          if (!isHover) closeMobileNav();
        }, 3000);
      }
    };

    const closeMobileNav = () => {
      clearInterval(closeMobileNavInterval);

      mobileNavBtn.setAttribute("aria-expanded", "false");
      mobileNav.classList.remove("open");
      document.removeEventListener("click", closeNavOnClickOutside);
      document.removeEventListener("keyup", closeNavOnClickOutside);
      document.removeEventListener("keyup", closeOnEsc);
    };

    const toggleMobileNav = (_event) => {
      if (mobileNav.classList.contains("open")) closeMobileNav();
      else openMobileNav();
    };

    const closeNavOnClickOutside = (_event) => {
      if (
        !mobileNavContent.contains(_event.target) &&
        !mobileNavBtn.contains(_event.target)
      ) {
        closeMobileNav();
      }
    };

    const closeOnEsc = (_event) => {
      if (_event.key === "Escape") closeMobileNav();
    };

    mobileNavBtn.addEventListener("click", toggleMobileNav);

    const params = window.location.search;
    if (params.length && params.includes("category_id")) {
      openMobileNav();
      const activeItem = mobileNav.getElementsByClassName(
        "js-header-nav-mobile-active"
      )[0];
      if (!activeItem) return;

      scrollNavToActiveEl(mobileNavContent, activeItem);
    }
  };

  const handleHeaderNavOverflow = (nav) => {
    const navContent = nav.firstChild;
    const mobileNav = document.getElementsByClassName(
      "js-header-nav-mobile"
    )[0];

    const showMobileNav = window.innerWidth <= 560 || isElOverflown(navContent);

    if (showMobileNav) {
      nav.style.display = "none";
      mobileNav.classList.add("visible");
      handleHeaderNavToggle(mobileNav);
    } else {
      setTimeout(function () {
        navContent.style.overflowX = "auto";
        navContent.style.justifyContent = "flex-end";
        navContent.style.maxWidth = "unset";
        nav.style.opacity = "1";
      }, 500);
    }
  };

  const handleHeaderNav = () => {
    const nav = document.getElementsByClassName("js-header-nav")[0];
    if (!nav) return;

    handleHeaderNavOverflow(nav);
  };

  const keyboardNavigationOn = (e) => {
    if (
      e.keyCode === 9 &&
      !document.body.classList.contains("keyboard-navigation-on")
    ) {
      // If tab is pressed.
      isKeyboardNavigationOn = true;
      clearInterval(closeMobileNavInterval);

      document.body.classList.add("keyboard-navigation-on");
      document.body.classList.remove("mouse-navigation-on");

      document.body.removeEventListener("keyup", keyboardNavigationOn);
      document.body.addEventListener("mousedown", mouseNavigationOn);

      carousel.onKeyboardNavigationOn();

      window.addEventListener("blur", iframeBlur);
    }
  };

  const mouseNavigationOn = (e) => {
    if (
      e.type === "mousedown" &&
      document.body.classList.contains("keyboard-navigation-on")
    ) {
      isKeyboardNavigationOn = false;

      document.body.classList.remove("keyboard-navigation-on");
      document.body.classList.add("mouse-navigation-on");

      document.body.removeEventListener("mousedown", mouseNavigationOn);
      document.body.addEventListener("keyup", keyboardNavigationOn);

      carousel.onKeyboardNavigationOff();

      window.removeEventListener("blur", iframeBlur);
      window.removeEventListener("focus", iframeFocus);
    }
  };

  const iframeBlur = () => {
    window.addEventListener("focus", iframeFocus);
    window.removeEventListener("blur", iframeBlur);
    carousel.onKeyboardNavigationOff();
  };

  const iframeFocus = () => {
    window.addEventListener("blur", iframeBlur);
    window.removeEventListener("focus", iframeFocus);
    carousel.onKeyboardNavigationOn();
  };

  const init = () => {
    sendWidgetDimensions();

    parentSender.ready().then(() => {
      parentSender.send({
        widgetReady: true
      });
    });

    statistic.signature = promotions.signature;

    if (widgetVersion === "iFrame" && !widgetViewSent) {
      statistic.sendWidgetView();
      widgetViewSent = true;
    }

    /* In case there are no promotions */
    if (promotions.promotionsLength() === 0) {
      const noItemsBlockNode = templater.noItemsBlock();
      if (mainSliderContainer.children.length) {
        mainSliderContainer.insertBefore(
          noItemsBlockNode,
          mainSliderContainer.children[0]
        );
      } else {
        mainSliderContainer.appendChild(noItemsBlockNode);
      }

      sendWidgetDimensions();

      window.addEventListener(
        "resize",
        () => {
          if (globalIDAnimationFrame) {
            window.cancelAnimationFrame(globalIDAnimationFrame);
            globalIDAnimationFrame = null;
          }

          globalIDAnimationFrame = window.requestAnimationFrame(() => {
            sendWidgetDimensions();
          });
        },
        false
      );

      /* Don't send widget statistics when widget is not in the viewport */
      parentReceiver.subscribe((event) => {
        if (
          event.data.from === "dealsParentWindow" &&
          event.data.inViewport &&
          !widgetViewSent
        ) {
          statistic.sendWidgetView();
          widgetViewSent = true;
        }
      });

      return false;
    }

    /* In case there are promotions */
    const slidesContainer = carousel.getSlidesContainer();

    const loadingBlockNode = document.createElement("div");
    loadingBlockNode.classList.add(
      "main-tiles-unit-wrapper",
      "js-loading-block"
    );
    loadingBlockNode.style.width = "100%";
    loadingBlockNode.innerHTML = Templater.loadingBlock();

    updateLayoutBasedOnItemsPerPage();

    const getPromotions = ({ withPreload } = { withPreload: false }) => {
      const loadingBlockNodeClone = loadingBlockNode.cloneNode(true);
      slidesContainer.appendChild(loadingBlockNodeClone);

      return promotions
        .getItems({
          withPreload
        })
        .then((data) => {
          loadingBlockNodeClone.parentNode.removeChild(loadingBlockNodeClone);

          return data;
        })
        .then(getFirstPromotionsIds)
        .then(generatePromotionsNodes)
        .then((tilesNodes) => carousel.addSlides(tilesNodes));
    };

    getPromotions({
      withPreload: true
    });

    /* On slides are added to carousel */
    mainSliderContainer.addEventListener(
      "add-slides.after.innocode-carousel",
      () => {
        if (!sliderInitialized) {
          sliderInitialized = true;
          Templater.uploadImages(mainSliderContainer);
          initTilesResponsiveness(mainSliderContainer, isIFrameHeightSet);
          statistic.addToView(firstPromotionsIds, false);
        }

        sendWidgetDimensions();
      },
      false
    );

    /* On click on the next slide/ on slide rotate, start */
    mainSliderContainer.addEventListener(
      "click.before.next.innocode-carousel.slide",
      () => {
        getPromotions().then(() => {
          Templater.uploadImages(mainSliderContainer);
          Templater.setTilesResponsiveness(
            mainSliderContainer,
            isIFrameHeightSet
          );
        });
      },
      false
    );

    const addToStatistic = (event) => {
      const data = event.detail;
      const promotionsIds = data.currentSlidesNodes.map((node) => {
        const promotionNode = node.querySelector("a[data-id]");
        if (promotionNode) {
          const promotionId = promotionNode.getAttribute("data-id");
          return parseInt(promotionId, 10);
        }

        return null;
      });

      statistic.addToView(
        promotionsIds.filter((id) => id !== null && !Number.isNaN(id)),
        promotions.isLastPromotions(promotionsIds)
      );
    };

    /* On click on the next slide, end */
    mainSliderContainer.addEventListener(
      "click.after.next.innocode-carousel.slide",
      addToStatistic,
      false
    );

    /* On click on the previous slide */
    mainSliderContainer.addEventListener(
      "click.after.prev.innocode-carousel.slide",
      addToStatistic,
      false
    );

    /* On click on promotion tile */
    mainSliderContainer.addEventListener(
      "click",
      (event) => {
        const node = helpers.getParentByClassName(event.target, "tile");
        const itemId = node && node.getAttribute("data-id");
        if (itemId) {
          statistic.sendClick([itemId]).catch(generateErrorMessage);
        }
      },
      false
    );

    /* Don't send statistics and don't autorotate when widget is not in the viewport */
    parentReceiver.subscribe((event) => {
      if (
        event.data.from === "dealsParentWindow" &&
        typeof event.data.inViewport !== "undefined"
      ) {
        if (event.data.inViewport) {
          carousel.initAutoRotate();
          statistic.startSendStatisticInterval();

          if (!widgetViewSent) {
            statistic.sendWidgetView();
            widgetViewSent = true;
          }
        } else {
          statistic.endSendStatisticInterval();
          carousel.deinitAutoRotate();
        }
      }
    });

    return Promise.resolve(true);
  };

  window.addEventListener("DOMContentLoaded", () => {
    handleHeaderNav();

    document.body.addEventListener("keyup", keyboardNavigationOn);
    document.body.addEventListener("mousedown", mouseNavigationOn);
  });

  return promotions.ready().then(init).catch(generateErrorMessage);
};
